import React from 'react';
import { X, AlertCircle, ChevronRight } from 'lucide-react';
import { LTMatch } from '@shared/hooks/useLanguageTool';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/lib/utils';
import { Badge } from '@shared/components/ui/badge';

interface GrammarPanelProps {
  matches: LTMatch[];
  isChecking: boolean;
  error: string | null;
  onApplySuggestion: (match: LTMatch, replacement: string) => void;
  onClose: () => void;
}

const CATEGORY_COLOR: Record<string, string> = {
  GRAMMAR: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
  TYPOS: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400',
  STYLE: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  PUNCTUATION: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
};

export default function GrammarPanel({
  matches,
  isChecking,
  error,
  onApplySuggestion,
  onClose,
}: GrammarPanelProps) {
  return (
    <aside className="w-72 h-full flex flex-col border-l border-border bg-background shrink-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm">Grammar</h3>
          {!isChecking && matches.length > 0 && (
            <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 rounded-full px-1.5 py-0.5 font-medium">
              {matches.length}
            </span>
          )}
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2">
        {isChecking ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
            <div className="h-5 w-5 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
            <p className="text-sm">Checking grammar…</p>
          </div>
        ) : error ? (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm mt-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        ) : matches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
            <span className="text-3xl">✅</span>
            <p className="text-sm">No issues found</p>
          </div>
        ) : (
          <div className="space-y-2 py-1">
            {matches.map((match, i) => (
              <MatchCard
                key={i}
                match={match}
                onApply={(r) => onApplySuggestion(match, r)}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

function MatchCard({
  match,
  onApply,
}: {
  match: LTMatch;
  onApply: (replacement: string) => void;
}) {
  const category = match.rule.category.name.toUpperCase();
  const colorClass = CATEGORY_COLOR[category] ?? 'bg-muted text-muted-foreground';

  // Highlight the error in context
  const { text, offset, length } = match.context;
  const before = text.slice(0, offset);
  const error = text.slice(offset, offset + length);
  const after = text.slice(offset + length);

  return (
    <div className="rounded-lg border border-border p-3 space-y-2 hover:bg-accent/30 transition-colors">
      {/* Category badge */}
      <div className="flex items-center gap-1.5">
        <span className={cn('text-xs font-medium rounded px-1.5 py-0.5', colorClass)}>
          {match.rule.category.name}
        </span>
      </div>

      {/* Context snippet */}
      <p className="text-xs text-muted-foreground font-mono leading-relaxed">
        {before}
        <span className="bg-red-200 dark:bg-red-900/50 text-red-800 dark:text-red-300 rounded px-0.5">
          {error}
        </span>
        {after}
      </p>

      {/* Message */}
      <p className="text-xs text-foreground">{match.message}</p>

      {/* Suggestions */}
      {match.replacements.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {match.replacements.slice(0, 4).map((r, i) => (
            <button
              key={i}
              className="text-xs px-2 py-0.5 rounded border border-border bg-background hover:bg-accent transition-colors font-medium flex items-center gap-1"
              onClick={() => onApply(r.value)}
            >
              <ChevronRight className="h-2.5 w-2.5 text-muted-foreground" />
              {r.value}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
